import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import { CashFlowCategory, CashFlowClassification as Row } from '../../interfaces/accounting.interface';
import { CashFlowService } from '../../services/cashFlow.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { humanizeEnum } from '../../utils/format';
import { ConfirmDialog } from '../common/ConfirmDialog';

const CATEGORIES: CashFlowCategory[] = ['CASH_EQUIVALENT', 'OPERATING', 'INVESTING', 'FINANCING', 'NON_CASH'];
const AUTO = '__AUTO__';

/** Bulk "Cash flow classification" page: balance-sheet accounts with derived vs explicitly set category. */
const CashFlowClassification: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const [rows, setRows] = useState<Row[]>([]);
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [filter, setFilter] = useState('');
    const [onlyUnclassified, setOnlyUnclassified] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pending, setPending] = useState<{ row: Row; value: string } | null>(null);

    const load = async () => {
        try {
            setRows(await CashFlowService.getClassifications());
            setEdits({});
        } catch (err) {
            toast.showApiError(err, 'Failed to load classifications');
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return rows.filter(r => (!q || r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
            && (!onlyUnclassified || r.effectiveCategory === null));
    }, [rows, filter, onlyUnclassified]);

    const unclassifiedCount = rows.filter(r => r.effectiveCategory === null).length;
    const valueFor = (r: Row) => edits[r.accountId] ?? r.overrideCategory ?? AUTO;

    // A header's category cascades to every un-overridden account under it: confirm before applying one.
    const changeCategory = (r: Row, value: string) => {
        if (value !== AUTO && (r.affectedDescendants ?? 0) > 0) {
            setPending({ row: r, value });
            return;
        }
        setEdits(prev => ({ ...prev, [r.accountId]: value }));
    };

    const save = async () => {
        const items = Object.entries(edits).map(([accountId, v]) => ({
            accountId, category: v === AUTO ? null : (v as CashFlowCategory),
        }));
        if (items.length === 0) return;
        try {
            setSaving(true);
            const result = await CashFlowService.updateClassifications(items);
            if (result.updated > 0) toast.showSuccess(`${result.updated} account(s) updated`);
            await load();
            if (result.failures.length > 0) {
                // Keep the rows that failed so they can be fixed; the others are already saved.
                const failed = new Set(result.failures.map(f => f.accountId));
                setEdits(Object.fromEntries(items.filter(i => failed.has(i.accountId))
                    .map(i => [i.accountId, i.category ?? AUTO])));
                toast.showError(`${result.failures.length} account(s) not saved: ${result.failures[0].message}`);
            }
        } catch (err) {
            toast.showApiError(err, 'Failed to save classification');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box data-help-page="accounting.cashFlowClass.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Cash Flow Classification</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                Where each balance-sheet account&apos;s movement lands on the cash flow statement. Your choice here applies to
                this institution only; &quot;Automatic&quot; follows the chart default or the built-in rules.
            </Typography>
            {unclassifiedCount > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {unclassifiedCount} account(s) have no category and will be left out of the statement.
                </Alert>
            )}
            <Box data-help-section="accounting.cashFlowClass.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <TextField data-help="accounting.cashFlowClass.search" placeholder="Search code or name" size="small" value={filter} onChange={e => setFilter(e.target.value)}
                    inputProps={{ 'aria-label': 'Search accounts' }} sx={{ flex: '1 1 220px', maxWidth: 320 }} />
                <Button data-help="accounting.cashFlowClass.onlyUnclassified" variant={onlyUnclassified ? 'contained' : 'outlined'} onClick={() => setOnlyUnclassified(v => !v)}>
                    Unclassified only
                </Button>
                {canManage && (
                    <Button data-help="accounting.cashFlowClass.save" variant="contained" onClick={save} disabled={saving || Object.keys(edits).length === 0}>
                        {saving ? 'Saving...' : `Save changes (${Object.keys(edits).length})`}
                    </Button>
                )}
            </Box>
            <TableContainer data-help-section="accounting.cashFlowClass.table" component={Paper} variant="outlined">
                <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.cashFlowClass.col.code">Code</TableCell>
                            <TableCell data-help="accounting.cashFlowClass.col.account">Account</TableCell>
                            <TableCell data-help="accounting.cashFlowClass.col.effective">Effective</TableCell>
                            <TableCell data-help="accounting.cashFlowClass.col.origin">Origin</TableCell>
                            <TableCell data-help="accounting.cashFlowClass.col.set" sx={{ minWidth: 200 }}>Set category</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map(r => (
                            <TableRow key={r.accountId}>
                                <TableCell>{r.code}</TableCell>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>
                                    {r.effectiveCategory
                                        ? humanizeEnum(r.effectiveCategory)
                                        : <Chip size="small" color="warning" label="Unclassified" />}
                                </TableCell>
                                <TableCell>{r.source === 'NONE' ? '—' : humanizeEnum(r.source)}</TableCell>
                                <TableCell>
                                    <TextField data-help="accounting.cashFlowClass.rowCategory" select size="small" fullWidth value={valueFor(r)}
                                        disabled={!canManage || (r.rootHeader === true && valueFor(r) === AUTO)}
                                        helperText={r.rootHeader ? 'Top-level account: classify the accounts under it' : undefined}
                                        inputProps={{ 'aria-label': `Category for ${r.code}` }}
                                        onChange={e => changeCategory(r, e.target.value)}>
                                        <MenuItem value={AUTO}>Automatic (derived)</MenuItem>
                                        {CATEGORIES.map(c => <MenuItem key={c} value={c}>{humanizeEnum(c)}</MenuItem>)}
                                    </TextField>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmDialog
                open={pending !== null}
                title="Apply to the accounts below?"
                message={pending ? `${pending.row.code} ${pending.row.name} is a group account. Setting its category to ${humanizeEnum(pending.value)} also applies to the ${pending.row.affectedDescendants} account(s) under it that have no category of their own.` : ''}
                confirmLabel="Apply"
                onCancel={() => setPending(null)}
                onConfirm={() => {
                    if (pending) setEdits(prev => ({ ...prev, [pending.row.accountId]: pending.value }));
                    setPending(null);
                }}
            />
        </Box>
    );
};

export default CashFlowClassification;
